import { Project } from '@griply/assets';
import { GoalType } from '@griply/types';
import { hasGoalParent } from '@griply/utils';
import {
  useConvertGoalType,
  useCreateSubGoalAllowed,
  useCreateUnlimitedGoalsAllowed,
  useSubGoals,
} from 'features/goals';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Target } from 'shared/assets/icons';
import { Icon } from 'shared/components/ui/icon';
import {
  PopupMenuButton,
  PopupMenuList,
  PopupMenuListItem,
} from 'shared/components/ui/popup-menu';
import { useOpenPremiumDialog } from 'shared/contexts/premium-dialog';
import { useEntityLinks } from 'shared/hooks/use-entity-links';
import { Goal } from 'shared/types/goal';

export type ConvertGoalTypeListProps = {
  goal: Goal;
  onClose: () => void;
  openConvertBlocked: () => void;
};

export const ConvertGoalTypeList: React.FC<ConvertGoalTypeListProps> = ({
  goal,
  onClose,
  openConvertBlocked,
}) => {
  const { t } = useTranslation();
  const links = useEntityLinks();
  const convertGoalType = useConvertGoalType();
  const openPremiumDialog = useOpenPremiumDialog();

  const isProject = goal.type === GoalType.Project;
  const subGoals = useSubGoals(goal.id);
  // A project is always a leaf, so a goal can only become one once it is childless.
  const convertBlocked = !isProject && !!subGoals.length;

  // Converting counts against the destination type's limit.
  const { allowed: createGoalAllowed } = useCreateUnlimitedGoalsAllowed();
  const { allowed: createSubGoalAllowed } = useCreateSubGoalAllowed();
  const { allowed: createProjectAllowed } = useCreateUnlimitedGoalsAllowed(
    GoalType.Project,
  );
  const convertToGoalAllowed = hasGoalParent(goal, { links })
    ? createSubGoalAllowed
    : createGoalAllowed;
  const convertAllowed = isProject
    ? convertToGoalAllowed
    : createProjectAllowed;

  const onConvertGoalType = (e: React.MouseEvent) => {
    e.preventDefault();
    if (convertBlocked) {
      openConvertBlocked();
      return;
    }
    onClose();
    if (!convertAllowed) {
      openPremiumDialog();
      return;
    }
    convertGoalType(goal);
  };

  return (
    <PopupMenuList hasBottomBorder>
      <PopupMenuListItem>
        <PopupMenuButton
          onClick={onConvertGoalType}
          isPremium={!convertAllowed}
          start={<Icon icon={isProject ? Target : Project} />}
        >
          {t(
            isProject
              ? 'context-menus.goal.labels.turn-into-goal'
              : 'context-menus.goal.labels.turn-into-project',
          )}
        </PopupMenuButton>
      </PopupMenuListItem>
    </PopupMenuList>
  );
};
